/**
 * useGameState Hook
 * 
 * Manages core game state: score, current question, and game flow
 */

import { useState, useCallback } from 'react';
import { QUESTION_LIMITS } from '../constants/gameplay';

/**
 * Hook for managing game state
 * @returns {Object} Game state and management functions
 */
export const useGameState = () => {
  const [score, setScore] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showQuestionSelector, setShowQuestionSelector] = useState(false);
  const [selectedQuestionCount, setSelectedQuestionCount] = useState(QUESTION_LIMITS.DEFAULT_COUNT);
  const [customQuestionCount, setCustomQuestionCount] = useState('');
  const [pendingGame, setPendingGame] = useState(null);

  // Reset game state
  const resetGame = useCallback(() => {
    setScore(0);
    setCurrentQuestion(0);
  }, []);

  // Start game with selected question count
  const startGameWithCount = useCallback((onStart) => {
    let questionCount;
    
    if (selectedQuestionCount === 'custom') {
      // If custom is selected but no number entered, use default
      if (!customQuestionCount || customQuestionCount.trim() === '') {
        questionCount = QUESTION_LIMITS.DEFAULT;
      } else {
        questionCount = parseInt(customQuestionCount, 10);
      }
    } else {
      questionCount = selectedQuestionCount;
    }
    
    // Validate the question count
    if (isNaN(questionCount) || questionCount < QUESTION_LIMITS.MIN || questionCount > QUESTION_LIMITS.MAX) {
      return { 
        success: false, 
        error: `Please choose a number between ${QUESTION_LIMITS.MIN} and ${QUESTION_LIMITS.MAX}` 
      };
    }
    
    setShowQuestionSelector(false);
    setCurrentQuestion(0);
    setScore(0);
    setPendingGame(null);
    setCustomQuestionCount('');
    
    if (onStart) {
      onStart();
    }
    
    return { success: true, questionCount };
  }, [selectedQuestionCount, customQuestionCount]);

  return {
    // State
    score,
    setScore,
    currentQuestion,
    setCurrentQuestion,
    showQuestionSelector,
    setShowQuestionSelector,
    selectedQuestionCount,
    setSelectedQuestionCount,
    customQuestionCount,
    setCustomQuestionCount,
    pendingGame,
    setPendingGame,
    
    // Actions
    resetGame,
    startGameWithCount
  };
};

